import { useCallback, useEffect, type ReactNode } from "react";
import { Pressable } from "react-native";
import { createPortal } from "react-dom";
import { Image as ExpoImage } from "expo-image";
import { useUnistyles } from "react-native-unistyles";
import { Text, View } from "@src/components";
import { Icon } from "@src/components/Icon";
import type { WasteSenseEventRow } from "../types";
import { isPriorityIncident } from "../priority";
import type { EventSnapshotLightboxProps } from "./EventSnapshotLightbox.types";
import { activityTone, formatEventTimestamp } from "./eventDisplay";

/**
 * Fullscreen snapshot lightbox for the events table. Mounts at
 * `document.body` via `createPortal` so its fixed-position overlay
 * escapes any clipping/transformed ancestors (the report itself is
 * inside a centered, max-width column).
 *
 * Closes on backdrop click and on Escape. The image uses
 * `contentFit="contain"` so the full frame is visible regardless of
 * aspect ratio — `Image` defaults to `cover`, which would crop.
 *
 * The metadata strip below the image renders the same fields the
 * table row does — same time format, same asset/camera stack, same
 * sentence-cased activity badge — by routing both through the
 * shared helpers in `./eventDisplay`. They can't drift apart.
 */
export function EventSnapshotLightbox({ event, imageUrl, timezone, onClose }: EventSnapshotLightboxProps) {
  const { theme } = useUnistyles();

  const isOpen = event !== null && imageUrl !== null;

  useEffect(() => {
    if (!isOpen) return;
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [isOpen, onClose]);

  /**
   * Block backdrop-close when the click originated inside the
   * image/metadata stack. Wrapping the inner content in its own
   * `Pressable` would also work, but a stop-propagation handler
   * keeps the markup flatter and avoids a nested pressable
   * intercepting native focus rings on the image.
   */
  const stop = useCallback((e: { stopPropagation: () => void }) => {
    e.stopPropagation();
  }, []);

  if (!isOpen) return null;
  if (typeof document === "undefined") return null;

  const overlay = (
    <Pressable
      onPress={onClose}
      accessibilityLabel="Close snapshot"
      style={{
        position: "fixed" as "absolute",
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: "rgba(0,0,0,0.78)",
        zIndex: 10000,
        alignItems: "center",
        justifyContent: "center",
        padding: 24,
      }}
    >
      <Pressable
        onPress={stop}
        style={{
          maxWidth: "min(1200px, 92vw)" as unknown as number,
          maxHeight: "92vh" as unknown as number,
          alignItems: "center",
          justifyContent: "center",
          gap: 12,
        }}
      >
        {/* Close button — visually anchored to the top-right of the lightbox stack. */}
        <View flexDirection="row" justifyContent="flex-end" style={{ width: "100%" }}>
          <Pressable
            onPress={onClose}
            accessibilityRole="button"
            accessibilityLabel="Close"
            style={{
              width: 36,
              height: 36,
              borderRadius: 18,
              backgroundColor: "rgba(255,255,255,0.12)",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <Icon name="close" size={20} color="#ffffff" />
          </Pressable>
        </View>

        <ExpoImage
          source={imageUrl}
          contentFit="contain"
          transition={120}
          style={{
            width: "min(1200px, 92vw)" as unknown as number,
            /**
             * Reserve roughly: 36 (close) + 12 (gap) + ~280 (meta panel
             * with five labelled rows) + 12 (gap) + 48 (page padding)
             * below the image so nothing clips against the viewport.
             */
            height: "calc(92vh - 388px)" as unknown as number,
            borderRadius: 8,
            backgroundColor: theme.colors.grey[200],
          }}
        />

        <SnapshotMeta event={event} timezone={timezone} />
      </Pressable>
    </Pressable>
  );

  return createPortal(overlay, document.body);
}

interface SnapshotMetaProps {
  event: WasteSenseEventRow;
  timezone: string;
}

/**
 * Mirrors the table row's columns one-for-one: time, asset/camera
 * stack, activity badge, description (+ interesting tags). Any
 * future column additions/renames in the table should be reflected
 * here too.
 *
 * Laid out as a labelled definition list rather than a header row +
 * paragraph — the lightbox is a focused inspection view, so each
 * field gets its own row with a short label for context. This also
 * makes the panel scannable without relying on column position
 * (operators are no longer reading it next to the table).
 */
function SnapshotMeta({ event, timezone }: SnapshotMetaProps) {
  const time = formatEventTimestamp(event.timestamp, timezone);
  const tone = activityTone(event.activity_type);

  /**
   * Force-white text. The backdrop is always near-black, so we can't
   * route this through `textReversed` (which inverts with the theme
   * and would render dark-on-dark in dark mode).
   */
  const onDark = { color: "#ffffff" } as const;
  const onDarkMuted = { color: "rgba(255,255,255,0.72)" } as const;

  return (
    <View
      gap="sm"
      px="md"
      py="md"
      rounded={8}
      style={{
        /**
         * Near-opaque so any table content visible through the
         * dimmed backdrop doesn't bleed through. The previous 0.55
         * blended into the page text behind and made the panel
         * unreadable when the lightbox sat over a populated table.
         */
        backgroundColor: "rgba(15,15,18,0.94)",
        borderWidth: 1,
        borderColor: "rgba(255,255,255,0.10)",
        width: "100%",
        maxWidth: 880,
      }}
    >
      <MetaRow label="Time" labelStyle={onDarkMuted}>
        <Text textStyle="sm/regular" style={onDark}>
          {time}
        </Text>
      </MetaRow>

      <MetaRow label="Camera" labelStyle={onDarkMuted}>
        <View flexDirection="row" alignItems="baseline" gap="sm" style={{ flexWrap: "wrap" }}>
          <Text textStyle="sm/semibold" style={onDark}>
            {event.asset_id}
          </Text>
          <Text textStyle="xs/regular" style={onDarkMuted}>
            {event.camera}
          </Text>
        </View>
      </MetaRow>

      <MetaRow label="Activity" labelStyle={onDarkMuted}>
        <View flexDirection="row" gap="sm" style={{ flexWrap: "wrap" }}>
          <View
            alignSelf="flex-start"
            px="sm"
            py="xxs"
            rounded={999}
            /**
             * Same `activityTone` mapping the table uses, but rendered
             * with translucent overlays so the badge remains readable
             * against the dark backdrop. Keeps red/grey/etc. semantics
             * without dragging in the theme's surface colours.
             */
            style={{ backgroundColor: badgeBgForTone(tone.bg) }}
          >
            <Text textStyle="xs/semibold" style={badgeTextStyle(tone.bg)}>
              {event.activity_type}
            </Text>
          </View>
          {isPriorityIncident(event) ?
            /**
             * Mirrors the table row's "Priority" pill and the hero
             * list's classification — same `isPriorityIncident`
             * predicate the KPIs use. A reader inspecting the
             * lightbox should see the same priority status they saw
             * in the underlying row.
             */
            <View
              alignSelf="flex-start"
              px="sm"
              py="xxs"
              rounded={999}
              style={{ backgroundColor: "rgba(220,38,38,0.92)" }}
            >
              <Text
                textStyle="xs/semibold"
                style={{ color: "#ffffff", textTransform: "uppercase", letterSpacing: 0.6 }}
              >
                Priority
              </Text>
            </View>
          : null}
        </View>
      </MetaRow>

      <MetaRow label="Description" labelStyle={onDarkMuted}>
        <Text textStyle="sm/regular" style={onDark}>
          {event.description}
        </Text>
      </MetaRow>

      {event.interesting_tags.length > 0 ?
        <MetaRow label="Tags" labelStyle={onDarkMuted}>
          <Text textStyle="xs/regular" style={onDarkMuted}>
            {event.interesting_tags.join(" · ")}
          </Text>
        </MetaRow>
      : null}
    </View>
  );
}

interface MetaRowProps {
  label: string;
  labelStyle: { color: string };
  children: ReactNode;
}

/**
 * One row of the lightbox metadata panel. The label column has a
 * fixed width on wide viewports so values align vertically; on
 * narrow viewports the row wraps and the label drops above the
 * value (same `flexWrap` behaviour the rest of the report uses).
 */
function MetaRow({ label, labelStyle, children }: MetaRowProps) {
  return (
    <View flexDirection="row" gap="md" style={{ flexWrap: "wrap" }}>
      <View style={{ width: 96 }}>
        <Text textStyle="xs/semibold" style={{ ...labelStyle, textTransform: "uppercase", letterSpacing: 0.6 }}>
          {label}
        </Text>
      </View>
      <View flex={1} style={{ minWidth: 0 }}>
        {children}
      </View>
    </View>
  );
}

/**
 * The table renders activity badges on a light surface using
 * theme-coloured fills (e.g. `bg="danger"`). On the lightbox's
 * black backdrop those colours would clash and lose contrast, so
 * map them to translucent dark/red/white overlays that read on
 * black while preserving the same red-vs-neutral distinction.
 */
function badgeBgForTone(bg: "muted" | "danger" | "warning" | "success"): string {
  if (bg === "danger") return "rgba(220,38,38,0.85)";
  if (bg === "warning") return "rgba(217,119,6,0.85)";
  if (bg === "success") return "rgba(22,163,74,0.85)";
  return "rgba(255,255,255,0.16)";
}

function badgeTextStyle(bg: "muted" | "danger" | "warning" | "success"): { color: string } {
  if (bg === "muted") return { color: "#ffffff" };
  return { color: "#ffffff" };
}
